<template>
    <div :class="{'no-scroll': (width < 1200 && showNavMobile) || (width < 1200 && showFilter) } " class="min-h-screen d-flex flex-column">
        <Navbar v-if="width >= 1200" key="navbar-main" :show="isAuthenticated"/>
        <NavbarMobile v-if="width < 1200" key="navbar-mobile" :show="isAuthenticated"/>
        <Toast :width="width" />
        <div :class="['flex-grow-1', 'with-fixed-navbar', { 'scrolled': isScrolled }]">
            <router-view/>
        </div>
        <BaseFooter/>

        <teleport v-if="loading" to="#modal">
            <Modal :fullscreen="true" style="z-index: 99;">
                <template v-slot:body>
                    <div class="d-flex flex-row h-100 justify-content-center align-items-center">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                            style="margin: auto; background: transparent; display: block; shape-rendering: auto;"
                            width="100px"
                            height="100px"
                            viewBox="0 0 100 100"
                            preserveAspectRatio="xMidYMid">
                            <g transform="translate(20 50)">
                                <circle
                                    cx="0"
                                    cy="0"
                                    r="6"
                                    fill="#aa0132">
                                    <animateTransform
                                        attributeName="transform"
                                        type="scale"
                                        begin="-0.375s"
                                        calcMode="spline"
                                        keySplines="0.3 0 0.7 1;0.3 0 0.7 1"
                                        values="0;1;0"
                                        keyTimes="0;0.5;1"
                                        dur="1s"
                                        repeatCount="indefinite"></animateTransform>
                                </circle>
                            </g><g transform="translate(40 50)">
                                <circle
                                    cx="0"
                                    cy="0"
                                    r="6"
                                    fill="#aa0132">
                                    <animateTransform
                                        attributeName="transform"
                                        type="scale"
                                        begin="-0.25s"
                                        calcMode="spline"
                                        keySplines="0.3 0 0.7 1;0.3 0 0.7 1"
                                        values="0;1;0"
                                        keyTimes="0;0.5;1"
                                        dur="1s"
                                        repeatCount="indefinite"></animateTransform>
                                </circle>
                            </g><g transform="translate(60 50)">
                                <circle
                                    cx="0"
                                    cy="0"
                                    r="6"
                                    fill="#aa0132">
                                    <animateTransform
                                        attributeName="transform"
                                        type="scale"
                                        begin="-0.125s"
                                        calcMode="spline"
                                        keySplines="0.3 0 0.7 1;0.3 0 0.7 1"
                                        values="0;1;0"
                                        keyTimes="0;0.5;1"
                                        dur="1s"
                                        repeatCount="indefinite"></animateTransform>
                                </circle>
                            </g><g transform="translate(80 50)">
                                <circle
                                    cx="0"
                                    cy="0"
                                    r="6"
                                    fill="#aa0132">
                                    <animateTransform
                                        attributeName="transform"
                                        type="scale"
                                        begin="0s"
                                        calcMode="spline"
                                        keySplines="0.3 0 0.7 1;0.3 0 0.7 1"
                                        values="0;1;0"
                                        keyTimes="0;0.5;1"
                                        dur="1s"
                                        repeatCount="indefinite"></animateTransform>
                                </circle>
                            </g>
                            <!-- [ldio] generated by https://loading.io/ -->
                        </svg>
                    </div>
                </template>
                <template v-slot:backdrop>
                    <div class="modal-backdrop show"></div>
                </template>
            </Modal>
        </teleport>
    </div>
</template>

<script lang="ts">
import {
    computed, onMounted, onUnmounted, ref, 
} from 'vue';
import BaseFooter from '@/components/BaseFooter.vue';
import Navbar from '@/components/Navbar.vue';
import NavbarMobile from '@/components/NavbarMobile.vue';
import Toast from '@/components/Toast.vue';
import {
    useAuthStore, useCartStore, useModalStore, useUIStore, 
} from '@/stores';
import Modal from '@/views/Modal.vue';

export default {
    components: {
        Navbar,
        NavbarMobile,
        Toast,
        BaseFooter,
        Modal,
    },
    setup() {
        const authStore = useAuthStore();
        const cartStore = useCartStore();
        const modalStore = useModalStore();
        const uiStore = useUIStore();
        const isAuthenticated = computed(() => authStore.isAuthenticated);
        const width = ref(window.innerWidth);
        const onWidthChange = () => width.value = window.innerWidth;
        const showNavMobile = computed(() => modalStore.getShowNavMobile);
        const showFilter = computed(() => modalStore.getShowFilter);
        const loading = computed(() => modalStore.isLoading);
        const isScrolled = computed(() => uiStore.getIsScrolled);

        onMounted(async () => {
            window.addEventListener('resize', onWidthChange);
            uiStore.initScrollListener();
            
            // Validate token on app startup if one exists
            if (authStore.authToken && !authStore.tokenValidated) {
                await authStore.validateToken();
            }
        });

        onUnmounted(() => {
            window.removeEventListener('resize', onWidthChange);
            uiStore.removeScrollListener();
        });

        if (authStore.isAuthenticated) {
            cartStore.updateCartCount();
        }

        return {
            isAuthenticated,
            width,
            showNavMobile,
            showFilter,
            loading,
            isScrolled,
        };
    },
};
</script>

<style lang="scss">
@import "~@/assets/variables";
html, body {
    max-width: 100%;
    overflow-x: hidden;
}

// Global styles
.no-scroll{
    height: 100vh;
    max-height: 100vh;
    overflow: hidden;
}

#app {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    position: relative;
    width: 100vw;
    overflow: hidden;
    text-align: center;
}

.clear {
    clear: both;
}

h1 {
    font-family: "DINPro", sans-serif;
}

.main {
    display: inline-block;
    background-color: $kwast-lightgray;
}

.inline-router-link {
    margin: 1.25rem 0 1.5625rem 0;
    font-family: 'DINPro', sans-serif;
    font-size: 16px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.63;
    letter-spacing: normal;
    text-align: center;
    color: #000000;

    &.small {
        font-size: .75rem;
        color: $kwast-gray;
    }
}

.red {
    color: $kwast-red;
}

.underline {
    text-decoration: underline;
}

.divider {
    height: 2px;
    max-width: 100%;
    width: auto;
    border: 1px solid #d8d8d8;
}

.clickable {
    cursor: pointer;
}

.swiper-horizontal > .swiper-scrollbar {
    height: 2px !important;
}

.swiper-scrollbar-drag {
    background: $kwast-red !important;
}

.light {
    color: $kwast-mediumgray;
    font-size: 0.75rem;
}

.medium {
    font-size: 0.75rem;
}

.opacity-0 {
    opacity: 0;
    cursor: default !important;
}

.circle {
    margin-left: 6px;
    display: inline-block;
    width: 11px;
    height: 11px;
    border-radius: 50%;

    &.green {
        background: $kwast-green;
    }

    &.orange {
        background: #fe934c;
    }

    &.red {
        background: $kwast-red;
    }
}

.v3dp__datepicker {
    width: 100%;

    .v3dp__popout {
        left: -50px;
    }

    input {
        background: $kwast-lightgray;
        border: none;
        height: 40px;
        padding: 0 12px;
        width: 80%;
    }
}

@media only screen and (max-width: 767px) {
    .v3dp__datepicker {
        input {
            width: 100%;
        }
    }
}

.min-h-screen {
    min-height: 100vh;
}

.flex-grow-1 {
    .container {
        min-height: 80vh;
    }
    
    &.with-fixed-navbar {
        padding-top: 100px;
        transition: padding-top 0.3s ease;
        
        &.scrolled {
            padding-top: 70px;
        }
    }
}

@media only screen and (max-width: 1199px) {
    .flex-grow-1 {
        &.with-fixed-navbar {
            padding-top: 100px;
            
            &.scrolled {
                padding-top: 100px; // Mobile navbar doesn't change height
            }
        }
    }
}
</style>
